import React, { useEffect, useMemo, useState } from 'react'
import MaterialTable, { MTableToolbar } from "material-table";
import { ToastContainer, toast } from "react-toastify";
import ContentLoader from 'react-content-loader';
import axios from "axios";

import { getTrans as t } from "../../Localization/translations";
import { API } from '../../backend';
import authHeader from '../../services/auth-header';
import RentMapping from './RentMapping';
import FeeMapping from './FeeMapping';

const toastOptions = {
    position: "top-right",
    autoClose: 1000,
    hideProgressBar: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
};

// API sends tax as a boolean; the table edits it as "Yes"/"No".
const toRow = (rule) => ({ ...rule, tax: rule.tax ? "Yes" : "No" });

// [{value, label}] from the API -> material-table lookup {value: label}
const toLookup = (options = []) =>
    Object.fromEntries(options.map(({ value, label }) => [value, t(label)]));

const errorMessage = (error) =>
    error?.response?.data?.error || error?.response?.data?.detail || "Something went wrong.";

const column = (title, field, extra = {}) => ({
    title: `${t(title)}`,
    field,
    headerStyle: {
        verticalAlign: "inherit",
        fontWeight: "bold",
        textAlign: "left",
    },
    cellStyle: {
        textAlign: "left",
        fontSize: 12,
    },
    render: (rowData) => (
        <>
            <div className="d-xl-none tc d-md-none d-lg-none">
                {t(title)}{" "}
            </div>
            <div>
                {t(rowData[field])}
            </div>
        </>
    ),
    ...extra,
});

const icons = {
    Edit: React.forwardRef((props, ref) => (
        <img
            {...props}
            ref={ref}
            src={process.env.PUBLIC_URL + "/static/images/ic-edit.svg"}
            alt="edit"
        />
    )),
};

const tableComponents = {
    Toolbar: (props) => (
        <div className="MuiToolbar-root MTableToolbar-spacer-21  my-temp-class">
            <MTableToolbar {...props} />
        </div>
    ),
};

const tableStyle = {
    backgroundColor: "var(--bs-table-bg)",
    borderBottomWidth: "1px",
    boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
};

const tableOptions = {
    headerStyle: {
        backgroundColor: "white",
        color: "#245C7F",
        fontSize: 12,
    },
    rowStyle: (rowData, index) => {
        if (index % 2) {
            return { backgroundColor: "#fff" };
        }
    },
    sorting: true,
    search: false,
    toolbar: true,
    paging: false,
    emptyRowsWhenPaging: false,
    actionsColumnIndex: -1,
};

const Products = () => {
    const [products, setProducts] = useState([]);
    const [choices, setChoices] = useState({});
    const [categoryCodes, setCategoryCodes] = useState([]);
    const [feeCodes, setFeeCodes] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios
            .get(API + "company/product-tax-settings/", { headers: authHeader() })
            .then((response) => {
                setProducts(
                    (response.data.data || []).map((product) => ({
                        ...product,
                        rules: product.rules.map(toRow),
                    }))
                );
                setChoices(response.data.choices || {});
                setCategoryCodes(response.data.category_codes || []);
                setFeeCodes(response.data.fee_codes || []);
            })
            .catch((error) => toast.error(errorMessage(error), toastOptions))
            .finally(() => setLoading(false));
    }, []);

    const columns = useMemo(() => [
        column("Transaction", "transaction", { editable: "never" }),
        column("Type", "line_type", { editable: "never" }),
        column("Tax", "tax", { lookup: { Yes: t("Yes"), No: t("No") } }),
        column("Address", "address", { lookup: toLookup(choices.address) }),
        column("Tax Call", "tax_call", { lookup: toLookup(choices.tax_call) }),
        // Free text (Avalara taxCode) — shown as typed, never translated.
        column("Tax Code", "tax_code", {
            render: (rowData) => (
                <>
                    <div className="d-xl-none tc d-md-none d-lg-none">
                        {t("Tax Code")}{" "}
                    </div>
                    <div>{rowData.tax_code || ""}</div>
                </>
            ),
            editComponent: (props) => (
                <input
                    type="text"
                    className="form-control form-control-sm"
                    maxLength={30}
                    value={props.value || ""}
                    onChange={(e) => props.onChange(e.target.value)}
                />
            ),
        }),
    ], [choices]);

    const setProductTax = (id, tax) =>
        setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, tax } : p)));

    // Update the screen right away, save in the background, roll back on failure.
    const handleProductTaxChange = (product, value) => {
        const tax = value === "Yes";
        const previous = product.tax;
        setProductTax(product.id, tax);
        axios
            .patch(
                API + `company/product-tax-settings/${product.id}/`,
                { tax },
                { headers: authHeader() }
            )
            .catch((error) => {
                setProductTax(product.id, previous);
                toast.error(errorMessage(error), toastOptions);
            });
    };

    const handleRentRowsChange = (productId, rows) =>
        setCategoryCodes((prev) => [...prev.filter((code) => code.product_setting_id !== productId), ...rows]);

    const handleFeeRowsChange = (productId, rows) =>
        setFeeCodes((prev) => [...prev.filter((code) => code.product_setting_id !== productId), ...rows]);

    // Only the Invoice > Rent row (Item Category mapping) and the Invoice > Fees row (Fee Type
    // mapping) open a panel; every other row has no chevron.
    const isRentRow = (rowData) => rowData.transaction === "Invoice" && rowData.line_type === "Rent";
    const isFeesRow = (rowData) => rowData.transaction === "Invoice" && rowData.line_type === "Fees";
    const mappingPanel = (product) => (rowData) => {
        const rent = isRentRow(rowData);
        const fees = isFeesRow(rowData);
        return {
            disabled: !rent && !fees,
            icon: rent || fees ? undefined : () => null,
            tooltip: rent ? t("Rent Mapping") : fees ? t("Fees Mapping") : undefined,
            render: () =>
                rent ? (
                    <RentMapping
                        product={product}
                        codes={categoryCodes.filter((code) => code.product_setting_id === product.id)}
                        onRowsChange={handleRentRowsChange}
                    />
                ) : (
                    <FeeMapping
                        product={product}
                        codes={feeCodes.filter((code) => code.product_setting_id === product.id)}
                        onRowsChange={handleFeeRowsChange}
                    />
                ),
        };
    };

    const handleRuleUpdate = (product) => (newData, oldData) =>
        axios
            .patch(
                API + `company/product-tax-rules/${oldData.id}/`,
                {
                    tax: newData.tax === "Yes",
                    address: newData.address,
                    tax_call: newData.tax_call,
                    tax_code: (newData.tax_code || "").trim(),
                },
                { headers: authHeader() }
            )
            .then((response) => {
                const updated = toRow(response.data.data);
                setProducts((prev) =>
                    prev.map((p) =>
                        p.id === product.id
                            ? { ...p, rules: p.rules.map((r) => (r.id === updated.id ? updated : r)) }
                            : p
                    )
                );
                toast.success(t("Tax rule updated successfully."), toastOptions);
            })
            .catch((error) => {
                toast.error(errorMessage(error), toastOptions);
                // Keep the row in edit mode so the user can retry.
                return Promise.reject(error);
            });

    return (
        <>
            <div className="page-wrapper">
                <ToastContainer />
                <div className="content container-fluid">
                    <div className="row">
                        <div className="header pt-3 pb-2">
                            <h5 className="title mb-0 ">{t("Products")}</h5>
                        </div>
                    </div>

                    {loading ? (
                        <ContentLoader
                            viewBox="0 0 400 160"
                            backgroundColor="rgba(0,0,0,0.06)"
                            foregroundColor="rgba(0,0,0,0.12)"
                        >
                            <rect x="0" y="13" rx="4" ry="4" width="400" height="9" />
                            <rect x="0" y="29" rx="4" ry="4" width="100" height="8" />
                            <rect x="0" y="50" rx="4" ry="4" width="400" height="10" />
                            <rect x="0" y="65" rx="4" ry="4" width="400" height="10" />
                            <rect x="0" y="79" rx="4" ry="4" width="100" height="10" />
                            <rect x="0" y="99" rx="5" ry="5" width="400" height="200" />
                        </ContentLoader>
                    ) : products.map((product) => (
                        <div className="row" key={product.id}>
                            <div className="col-md-12">
                                <div className="card mb-2">
                                    <div className="card-body px-4 py-3 d-flex align-items-center justify-content-between flex-wrap gap-3">
                                        <h6 className="mb-0 ">
                                            {t(product.product)}
                                        </h6>
                                        <div className="d-flex align-items-center gap-3">
                                            <label className="mb-0 ">{t("Tax")}</label>
                                            <select
                                                className="form-select"
                                                style={{ width: "100px" }}
                                                value={product.tax ? "Yes" : "No"}
                                                onChange={(e) => handleProductTaxChange(product, e.target.value)}
                                            >
                                                <option value="Yes">{t("Yes")}</option>
                                                <option value="No">{t("No")}</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {product.tax && (
                                    <div className="body mb-3">
                                        <div className="p-0">
                                            <MaterialTable
                                                style={tableStyle}
                                                title={
                                                    <h5 className="d-lg-flex">{`${t(product.product)} ${t("Transactions")}`}</h5>
                                                }
                                                columns={columns}
                                                data={product.rules}
                                                icons={icons}
                                                editable={{
                                                    onRowUpdate: handleRuleUpdate(product),
                                                }}
                                                detailPanel={[mappingPanel(product)]}
                                                components={tableComponents}
                                                options={tableOptions}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    )
}

export default Products
